import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { BarChart3, Save, RotateCcw, Loader2, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAT_FIELDS = [
  { key: 'totalUsers', label: 'Total Users', type: 'number' },
  { key: 'totalServicesCompleted', label: 'Services Completed', type: 'number' },
  { key: 'totalProductsSold', label: 'Products Sold', type: 'number' },
  { key: 'totalTransactions', label: 'Total Transactions', type: 'number' },
  { key: 'averageRating', label: 'Average Rating', type: 'decimal', max: 5 },
  { key: 'totalReviews', label: 'Total Reviews', type: 'number' },
  { key: 'satisfactionRate', label: 'Satisfaction Rate (%)', type: 'number', max: 100 },
];

export default function SiteStatsEditor({ getAuthHeaders }) {
  const [currentStats, setCurrentStats] = useState({});
  const [overrides, setOverrides] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${API}/platform/trust-stats`);
        setCurrentStats(res.data);
      } catch (e) {
        // ignore
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Only send non-empty values
      const values = {};
      for (const [key, val] of Object.entries(overrides)) {
        if (val !== '' && val !== undefined) {
          values[key] = Number(val);
        }
      }
      await axios.put(`${API}/platform/trust-stats/override`, values, { headers: getAuthHeaders() });
      toast.success('Site stats updated! Changes take effect within 5 minutes.');
      // Refresh display
      const res = await axios.get(`${API}/platform/trust-stats`);
      setCurrentStats(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update stats');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    try {
      await axios.delete(`${API}/platform/trust-stats/override`, { headers: getAuthHeaders() });
      toast.success('Overrides cleared. Showing real data now.');
      setOverrides({});
      const res = await axios.get(`${API}/platform/trust-stats`);
      setCurrentStats(res.data);
    } catch (err) {
      toast.error('Failed to reset');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="site-stats-editor">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#FF3008]" />
            Site Statistics Control
          </CardTitle>
          <CardDescription>
            Set custom values for your landing page stats. Leave blank to use real database counts.
            Stats are hidden on the landing page when all values are 0.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6 flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">
              Only set values you can verify. Fake numbers erode customer trust.
              Override values will replace real data on the landing page.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {STAT_FIELDS.map(field => (
              <div key={field.key}>
                <Label className="text-sm font-medium text-gray-700">{field.label}</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    type="number"
                    step={field.type === 'decimal' ? '0.1' : '1'}
                    min="0"
                    max={field.max}
                    placeholder={`Current: ${currentStats[field.key] ?? 0}`}
                    value={overrides[field.key] ?? ''}
                    onChange={(e) => setOverrides(prev => ({ ...prev, [field.key]: e.target.value }))}
                    className="flex-1"
                    data-testid={`stat-${field.key}`}
                  />
                </div>
                <p className="text-xs text-gray-400 mt-0.5">
                  Real: {currentStats[field.key] ?? 0}
                </p>
              </div>
            ))}
          </div>
          <div className="flex gap-3 mt-6 pt-4 border-t">
            <Button
              onClick={handleSave}
              disabled={saving || Object.keys(overrides).filter(k => overrides[k] !== '').length === 0}
              className="bg-[#FF3008] hover:bg-[#E02800]"
              data-testid="save-stats"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
              Save Overrides
            </Button>
            <Button variant="outline" onClick={handleReset} disabled={saving} data-testid="reset-stats">
              <RotateCcw className="w-4 h-4 mr-1" />
              Reset to Real Data
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
